import React, { useState } from 'react'
import { useParams } from 'react-router'
import {
  Card,
  CardBody,
  CardFooter,
  FormText,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap'
import { RedirectButton } from 'components/atoms'
import { CardTextData } from 'components/molecules'
import { CustomerOrderShipmentStatusBadge } from 'domains/customer-order/atoms'
import CustomerShipmentPickupStatusBadge from 'domains/customer-shipment/atoms/pickup-status-badge'
import { CustomerOrderShipmentTrackingNumberForm } from 'domains/customer-order/organisms'
import PropTypes from 'prop-types'
import PriceUtil from 'utils/price-util'
import StringUtil from 'utils/string-util'
import CustomerOrderCodBadge from 'domains/customer-order/atoms/cod-badge'
import { LoaderButton } from 'components/molecules/index'
import TimeUtil from 'utils/time-util'
import { AFFILIATE_NAMES, DOCUMENT_TEMPLATES } from 'domains/customer-order/constants/customer-order-constants'

const propTypes = {
  shipment: PropTypes.shape({
    status                     : PropTypes.string,
    pickup_status              : PropTypes.string,
    recipient_name             : PropTypes.string,
    recipient_phone            : PropTypes.string,
    tracking_no                : PropTypes.string,
    destination_street_address : PropTypes.string,
    destination_kelurahan      : PropTypes.string,
    destination_kecamatan      : PropTypes.string,
    destination_kota           : PropTypes.string,
    destination_provinsi       : PropTypes.string,
    destination_kode_pos       : PropTypes.string,
    number                     : PropTypes.string,
    courier                    : PropTypes.string,
    courier_service_type       : PropTypes.string,
    weight                     : PropTypes.number,
    weight_type                : PropTypes.string,
    cost                       : PropTypes.number,
    insurance_cost             : PropTypes.number,
    pickup_start_date          : PropTypes.string,
    order                      : PropTypes.object,
    tl_box                     : PropTypes.shape({
      name   : PropTypes.string,
      width  : PropTypes.number,
      height : PropTypes.number,
      length : PropTypes.number
    })
  }),
  handleUpdateTrackingNumber    : PropTypes.func,
  handleClickPrintShippingLabel : PropTypes.func,
  handleTrackCustomerShipment   : PropTypes.func,
  isSubmitting                  : PropTypes.object,
  handleClickGenerateAwb        : PropTypes.func
}

/**
 * @param {object} shipment
 * @param {Function} handleUpdateTrackingNumber
 * @param {Function} handleTrackCustomerShipment
 * @param {object} isSubmitting
 */
const CustomerOrderShipmentDetailCard = ({
  shipment,
  isSubmitting,
  handleUpdateTrackingNumber,
  handleClickPrintShippingLabel,
  handleTrackCustomerShipment,
  handleClickGenerateAwb
}) => {
  const { id } = useParams()

  let order                                                 = shipment.order
  const [ selectedLabelTemplate, setSelectedLabelTemplate ] = useState(order?.affiliate_name === AFFILIATE_NAMES.ZALORA ? AFFILIATE_NAMES.ZALORA : AFFILIATE_NAMES.TINKERLUST)

  return (
    <Card>
      <CardBody>
        <div className='small text-uppercase text-light font-weight-bolder'>
          { 'Customer Shipment' }
        </div>
        <CardTextData
          label="Ship No."
          value={ shipment.number }
        />

        <div className='d-flex flex-column align-items-start'>
          {
            <UncontrolledButtonDropdown>
              <DropdownToggle
                color='primary'
                className='mb-3 text-capitalize'
                caret
              >
                { selectedLabelTemplate || 'Select Label Template' }
              </DropdownToggle>
              <DropdownMenu>
                {
                  Object.values(DOCUMENT_TEMPLATES).map((template) => {
                    const isTinkerlustTemplate    = template === DOCUMENT_TEMPLATES.TINKERLUST
                    const isAffiliateNameTemplate = template === order.affiliate_name

                    if (isTinkerlustTemplate || isAffiliateNameTemplate) {
                      return (
                        <DropdownItem
                          key={ `invoice-template-${template}` }
                          className='text-capitalize'
                          onClick={ () => setSelectedLabelTemplate(template) }
                        >
                          { template }
                        </DropdownItem>
                      )
                    }
                  })
                }
              </DropdownMenu>
            </UncontrolledButtonDropdown>
          }

          <LoaderButton
            color='info'
            disabled={ isSubmitting.print_address }
            isLoading={ isSubmitting.print_address }
            onClick={ () => handleClickPrintShippingLabel(selectedLabelTemplate) }
            text='Print Shipping Label'
          />
        </div>


        <CardTextData
          label='Status'
          value={ <CustomerOrderShipmentStatusBadge status={ shipment.status } /> }
        />
        <CardTextData
          label='Pickup Status'
          value={ <CustomerShipmentPickupStatusBadge status={ shipment.pickup_status } /> }
        />

        <CardTextData label='Tracking Number' value={ shipment.tracking_no  ? shipment.tracking_no :  '' } />
        {
          shipment.tracking_no ? (
            <div className='align-self-center mb-3'>
              <LoaderButton
                color='primary'
                size='sm'
                onClick={ handleTrackCustomerShipment }
                isLoading={ isSubmitting.track_shipment }
                text={ 'Track Shipment' }
              />
            </div>
          ) : (
            <div className='mb-3'>
              <CustomerOrderShipmentTrackingNumberForm
                handleSubmit={ handleUpdateTrackingNumber }
                isSubmitting={ isSubmitting.tracking_number }
              />
              <small className='text-muted'>{ 'use this to manually complete the shipment for self pickup case' }</small>
              <LoaderButton
                className='mt-3'
                block={ true }
                color='success'
                onClick={ handleClickGenerateAwb }
                isLoading={ isSubmitting.generate_awb }
                text={ 'Generate AWB' }
              />
              <FormText>
                { 'Tombol ' }
                <strong>
                  { 'Generate AWB' }
                </strong>
                { '  digunakan untuk melakukan generate AWB/Tracking No untuk shipment.' }
                <br />
                <ul>
                  <li>
                    <strong>{ 'Anter Aja: ' }</strong>
                    { 'saat menekan tombol maka sistem akan otomatis melakukan generate AWB/Tracking No.' }
                  </li>
                  <li>
                    <strong>{ 'Grab Express: ' }</strong>
                    { 'akan mengarahkan ke halaman Request pickup karena untuk GrabExpress AWB/Tracking No. akan otomatis digenerate setelah menjadwalkan pickup' }
                  </li>
                  <li>
                    <strong>{ 'JNE: ' }</strong>
                    { 'untuk JNE setelah generate AWB tidak perlu menjadwalkan request pickup karena jadwal pickup sudah otomatis dibuat saat generate AWB.' }
                  </li>
                </ul>

              </FormText>
            </div>
          )
        }
        <CardTextData label='Is Order COD?' value={ <CustomerOrderCodBadge isCod={ shipment.order.is_cod } /> } />
        <CardTextData label='Courier' value={ `${StringUtil.humanize(shipment.courier)} (${shipment.courier_service_type}) - ${shipment.destination_kota}` } />
        <CardTextData label='Weight' value={ `${shipment.weight} kg (${shipment.weight_type})` } />
        <CardTextData
          label='Box'
          value={ shipment.tl_box.name }
          tooltip={ <>
            <div>Recommended packing by system</div>
            <div>Width: { shipment.tl_box.width } cm</div>
            <div>Height: { shipment.tl_box.height } cm</div>
            <div>Length: { shipment.tl_box.length } cm</div>
          </>
          }
        />
        <CardTextData label='Cost' value={ PriceUtil.format(shipment.cost) } />
        <CardTextData label='Insurance Cost' value={ PriceUtil.format(shipment.insurance_cost) } />
        <CardTextData label='Pickup Start Date' value={ TimeUtil.format(shipment.pickup_start_date, 'DD MMM YY, HH:mm:ss') } />

        <hr />

        <CardTextData label='Street Address' value={ shipment.destination_street_address } />
        <CardTextData label='Kelurahan' value={ shipment.destination_kelurahan } />
        <CardTextData label='Kecamatan' value={ shipment.destination_kecamatan } />
        <CardTextData label='Kota' value={ shipment.destination_kota } />
        <CardTextData label='Provinsi' value={ shipment.destination_provinsi } />
        <CardTextData label='Kode Pos' value={ shipment.destination_kode_pos } />
        <CardTextData label='Nama Penerima' value={ shipment.recipient_name } />
        <CardTextData label='Telephone' value={ shipment.recipient_phone } />
      </CardBody>
      <CardFooter>
        <div>
          <RedirectButton
            block={ true }
            color='warning'
            text={ 'Edit Address' }
            path={ `/app/customer-shipments/${id}/edit-address` }
            state={ shipment }
          />
        </div>
      </CardFooter>
    </Card>
  )}
CustomerOrderShipmentDetailCard.propTypes = propTypes

export default CustomerOrderShipmentDetailCard
